import React from 'react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { Button } from '@oysterjs/ui/button';
import { DialogActions, DialogDescription, DialogTitle, Dialog } from '@oysterjs/ui/dialog';
import {
  Dropdown,
  DropdownButton,
  DropdownDescription,
  DropdownDivider,
  DropdownHeader,
  DropdownItem,
  DropdownMenu
} from '@oysterjs/ui/dropdown';

import { DropdownItemText } from '~/components/dropdown-item-text';
import type { EmailTemplateChoice } from '~/api/data/email-templates/queries';
import { EMAIL_TEMPLATES_PATH } from '~/components/email-templates/email-template-paths';

/** A template to redraft the email with, or null to have Cara write it without one. */
export type TemplatePick = EmailTemplateChoice | null;

/**
 * Stays mounted and opens through `open` so its transition doesn't fight the closing menu's.
 * Keep editing takes focus on open: the dialog's tabindex=-1 root doesn't count as focusable, so
 * the Change menu would take focus back two frames after a pick.
 */
export const ReplaceEditsDialog = (props: {
  open: boolean;
  pick: TemplatePick;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  // The last pick keeps the wording steady while the dialog fades out.
  const shownPick = React.useRef(props.pick);
  if (props.open) shownPick.current = props.pick;
  const pick = shownPick.current;
  return (
    <Dialog open={props.open} onClose={props.onCancel} size="md">
      <DialogTitle>Replace your edits?</DialogTitle>
      <DialogDescription>
        {pick
          ? `Cara will redraft this email with the ${pick.name} template.`
          : 'Cara will rewrite this email without a template.'}{' '}
        Your changes to the email will be replaced. Recipients and attachments stay.
      </DialogDescription>
      <DialogActions>
        <Button
          type="button"
          color="white"
          className="whitespace-nowrap"
          autoFocus
          onClick={props.onCancel}
        >
          Keep editing
        </Button>
        <Button type="button" color="sky" className="whitespace-nowrap" onClick={props.onConfirm}>
          {pick ? 'Redraft' : 'Rewrite without a template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * The Template row's Change menu: No template first, then the agency's templates. Picking one
 * over a draft the agent has edited asks first.
 */
export const EmailTemplatePicker = (props: {
  choices: EmailTemplateChoice[] | undefined;
  hasChoicesLoadFailed: boolean;
  onRetryChoices: () => void;
  /** Why the email can't be redrafted right now, or undefined when it can. */
  disabledReason: string | undefined;
  /** Read when a template is picked, since edits don't re-render the composer. */
  hasEditedDraft: () => boolean;
  onPick: (pick: TemplatePick) => void;
}) => {
  const [pickToConfirm, setPickToConfirm] = React.useState<{ pick: TemplatePick } | null>(null);
  const choose = (pick: TemplatePick) => {
    if (props.hasEditedDraft()) setPickToConfirm({ pick });
    else props.onPick(pick);
  };

  return (
    <>
      <Dropdown>
        <DropdownButton
          plain
          disabled={!!props.disabledReason}
          title={props.disabledReason}
          className="px-1.5 py-0.5 text-xs text-sky-700 dark:text-sky-400"
        >
          Change
          <ChevronDownIcon data-slot="icon" />
        </DropdownButton>
        {/* z-50 because the email editor's toolbar is a sticky z-10. Opaque, since the shared
            menu's translucent fill takes the color of the email beneath. */}
        <DropdownMenu
          anchor="bottom start"
          className="z-50 max-h-[var(--anchor-max-height)] max-w-sm !bg-white dark:!bg-neutral-800"
        >
          <DropdownItem onClick={() => choose(null)}>
            <DropdownItemText>No template</DropdownItemText>
            <DropdownDescription>Cara writes the email freely, like today</DropdownDescription>
          </DropdownItem>
          {props.choices?.map((choice) => (
            <DropdownItem key={choice.id} onClick={() => choose(choice)}>
              <DropdownItemText className="min-w-0 truncate">{choice.name}</DropdownItemText>
              {choice.whenToUse && (
                <DropdownDescription className="truncate">{choice.whenToUse}</DropdownDescription>
              )}
            </DropdownItem>
          ))}
          {props.hasChoicesLoadFailed && (
            <>
              <DropdownHeader className="text-sm text-neutral-500 dark:text-neutral-400">
                We couldn't load your templates.
              </DropdownHeader>
              <DropdownItem onClick={props.onRetryChoices}>
                <DropdownItemText>Try again</DropdownItemText>
              </DropdownItem>
            </>
          )}
          <DropdownDivider />
          <DropdownItem href={EMAIL_TEMPLATES_PATH}>
            <DropdownItemText>Manage templates</DropdownItemText>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
      <ReplaceEditsDialog
        open={!!pickToConfirm}
        pick={pickToConfirm?.pick ?? null}
        onCancel={() => setPickToConfirm(null)}
        onConfirm={() => {
          setPickToConfirm(null);
          if (pickToConfirm) props.onPick(pickToConfirm.pick);
        }}
      />
    </>
  );
};
